import { useDialog, useToast } from '@repo/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { useNetworkState } from 'react-use';
import { fetchQuery } from 'relay-runtime';

import { PersonalDeductionContainerMutation } from '@/__generated__/PersonalDeductionContainerMutation.graphql';
import { refundHistoryIdQuery } from '@/__generated__/refundHistoryIdQuery.graphql';
import { useCollectErrorBoundary } from '@/components/tax-refund/collect/in-progress/CollectErrorBoundary';
import LoadingContent from '@/components/tax-refund/result/Request/LoadingContent';
import { indisRefundHistoryIdQuery } from '@/graphql/query/common/refund-history-id';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { refundTokenAtom } from '@/lib/stores/simple-auth';

const query = graphql`
  mutation PersonalDeductionContainerMutation($refundToken: String!, $refundHistoryId: String) {
    searchPersonalDeductionByLogin(refundToken: $refundToken, refundHistoryId: $refundHistoryId) {
      result
      errors {
        code
        message
      }
      taxpayerName
    }
  }
`;

export const PersonalDeductionContainer = () => {
  const relayEnv = useRelayEnvironment();
  const [commit, isLoading] = useMutation<PersonalDeductionContainerMutation>(query);
  const router = useRouter();
  const { showDialog } = useDialog();
  const { throwError } = useCollectErrorBoundary();
  const { toast } = useToast();
  const { online } = useNetworkState();
  const refundToken = useAtomValue(refundTokenAtom);

  const intervalId = useRef(0);
  const completedCollection = useRef(false);
  const retryCount = useRef(0);
  const alreadyToastOpened = useRef(false);
  const isStartGetRefundHistoryId = useRef(false);

  const after3Seconds = (callback?: () => void) => setTimeout(() => callback?.(), 3000);

  const handleExceptionError = useCallback(() => {
    completedCollection.current = true;
    after3Seconds(() => {
      throwError('ERR_ETC');
    });
  }, [throwError]);

  const getRefundHistoryId = useCallback(async () => {
    return new Promise<string>((resolve) => {
      if (isStartGetRefundHistoryId.current) return;

      isStartGetRefundHistoryId.current = true;

      const showErrorDialog = (dialogId: string) => {
        showDialog({
          id: dialogId,
          title: '인적공제 대상인지 다시한번 확인해 주세요.',
          buttons: {
            primary: {
              onClick: () => router.push(COMMON_PATHS.환급조회목록)
            }
          }
        });
      };
      fetchQuery<refundHistoryIdQuery>(relayEnv, indisRefundHistoryIdQuery, {}).subscribe({
        next: (res) => {
          if (!res || !!res?.getRefundHistoryId?.errors || !res?.getRefundHistoryId?.result) {
            showErrorDialog('refund-history-id-error-in-next');
            resolve('');
          }
          resolve(res?.getRefundHistoryId.refundHistoryId || '');
        },
        error: () => {
          showErrorDialog('refund-history-id-error-in-error');
          resolve('');
        }
      });
    });
  }, [relayEnv, router, showDialog]);

  const startPolling = useCallback(async () => {
    const refundHistoryId = await getRefundHistoryId();

    if (isLoading || !refundToken || !refundHistoryId) return;
    intervalId.current = window.setInterval(() => {
      commit({
        variables: {
          refundToken,
          refundHistoryId
        },
        onCompleted: (response, errors) => {
          const { searchPersonalDeductionByLogin: data } = response || {};
          if (errors || data?.errors?.code) {
            if (data.errors?.code === 'ERR_NO_EQUAL_TIN') {
              completedCollection.current = true;
              after3Seconds(() => {
                throwError('ERR_NO_EQUAL_TIN');
              });
            }
            handleExceptionError();
          }
          if (data?.result) {
            completedCollection.current = true;
            after3Seconds(() => {
              router.push(COMMON_PATHS.수집_완료);
            });
          }
        },
        onError: () => {
          handleExceptionError();
        },
        updater: () => {
          /** 네트워크 에러가 onError 까지 전달되지 않기 때문에 updater 에서 처리 */
          if (retryCount.current > 5) {
            after3Seconds(() => {
              throwError('NETWORK_DISCONNECT');
            });
            retryCount.current = 0;
            completedCollection.current = true;
            return;
          }
          if (!online) {
            ++retryCount.current;

            if (alreadyToastOpened.current) return;
            alreadyToastOpened.current = true;
            toast({
              title: '네트워크 불안정으로 다시 확인할게요.',
              variant: 'warning'
            });
          }
        }
      });
    }, 1000);
  }, [toast, online, commit, isLoading, throwError, router, refundToken, handleExceptionError, getRefundHistoryId]);

  const stopPolling = useCallback(() => {
    if (!intervalId.current) return;
    window.clearInterval(intervalId.current);
    intervalId.current = 0;
  }, []);

  useEffect(() => {
    if (completedCollection.current) return;
    startPolling();

    return stopPolling;
  }, [startPolling, stopPolling]);

  return <LoadingContent />;
};
